import { useCallback, useEffect, useRef, useState } from "react";
import { isPhoneNumberValid } from "utils/validHelper";
import { SignupPagePresenter } from "./SignupPresenter";
import { useLocation, useNavigate } from "react-router-dom";
import { postVerify } from "api/authAPI";
import { useMutation } from "react-query";
import { getTokenInfo } from "api/userAPI";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import {
  RESSELLER_USER_TYPE,
  SIGNUP_STEP,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import { checkEmpty, toTrim } from "utils/functions";

export const intialValidate: Validate = {
  firstName: false,
  lastName: false,
  password: false,
  confirmPassword: false,
  phoneNumber: false,
};

export function SignupPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { intlString, lang, notifyError, notifySuccess } = useApiError();

  const { pathname, state } = useLocation();
  const [checkedEmail, setCheckedEmail] = useState<string>("");
  const [checkedAccountId, setCheckedAccountId] = useState<string>("");

  const [step, setStep] = useState<SIGNUP_STEP>(SIGNUP_STEP.INPUT_INFO);
  const [userInfo, setUserInfo] = useState<SignupUserInfo>({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    parentAccount: "",
    agreement: false,
    use2faCode: "email",
    notifySms: true,
    isTrial: false,
    verifyPhone: false,
  });

  const [isValidated, setIsValidated] = useState<Validate>({
    firstName: false,
    lastName: false,
    password: false,
    confirmPassword: false,
    phoneNumber: false,
    agreement: false,
  });

  const [validateMsg, setValidateMsg] = useState<string>("");
  const [validateInputMsg, setValidateInputMsg] = useState<ValidateMsg>({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    agreement: "",
  });
  const [isResend, setIsResend] = useState<boolean>(false);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const agreementRef = useRef<HTMLInputElement>(null);
  // const firstNameRef = useRef<any>(null);

  const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
  const [isValidVerify, setIsValidVerify] = useState<boolean>(false);

  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);

  const [isLoadedToken, setIsLoadedToken] = useState<boolean>(false);
  const [isExpiredToken, setIsExpiredToken] = useState<boolean>(false);

  const [mfaNumber, setMfaNumber] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [isVerifyPhoneNumber, setIsVerifyPhoneNumber] = useState<boolean>(true);

  const getToken = async (token: string) => {
    getTokenInfo(token)
      .then((data) => {
        setCheckedEmail(data.result.email);
        setCheckedAccountId(data.result.accountId);
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            parentAccount: data.result.accountId as string,
            isTrial: data.result.isTrial,
            phoneNumber: data.result.phone,
            firstName: data.result.firstName || "",
            lastName: data.result.lastName || "",
          };
        });

        if (pathname === "/signup") {
          if (data.result.isActive) {
            setStep(SIGNUP_STEP.SUCCESS);
          } else {
            setStep(SIGNUP_STEP.INPUT_INFO);
          }
        }
        if (pathname === "/reset") {
          setStep(SIGNUP_STEP.RESET_PASSWORD);
        }
      })
      .catch((error: CustomError) => {
        const tokenEmail = error.response?.data?.result?.email;
        if (tokenEmail) {
          setCheckedEmail(tokenEmail);
        } else {
          const searchParams = new URLSearchParams(location.search);
          const email = searchParams.get("email");
          setCheckedEmail(email || "");
          // } else {
          //   setCheckedEmail("Email not found");
        }
        setIsExpiredToken(true);

        if (error.response?.data !== undefined) {
          if (pathname === "/reset") {
            if (error.response?.data.error === 1011) {
              setValidateMsg(intlString(lang.validateMsg.reset.getTokenExpire));
            } else if (error.response?.data.error === 1010) {
              setValidateMsg(
                intlString(lang.validateMsg.reset.getTokenInvalid)
              );
            } else {
              setValidateMsg(intlString(lang.validateMsg.signup.getTokenEmail));
            }
          } else {
            setStep(SIGNUP_STEP.EXPIRED_TOKEN);
            if (error.response?.data.error === 1011) {
              setValidateMsg(
                intlString(lang.validateMsg.signup.getTokenExpire)
              );
            } else if (error.response?.data.error === 1010) {
              setValidateMsg(
                intlString(lang.validateMsg.signup.getTokenInvalid)
              );
            } else if (error.response?.data.error === 10001) {
              setStep(SIGNUP_STEP.SUCCESS);
            } else {
              setValidateMsg(intlString(lang.validateMsg.signup.getTokenEmail));
            }
          }
        } else {
          setValidateMsg(intlString(lang.validateMsg.signup.getTokenEmail));
        }
      })
      .finally(() => {
        setIsLoadedToken(true);
      });
  };

  useEffect(() => {
    if (pathname.includes("/free-trial")) {
      setStep(SIGNUP_STEP.FREE_TRIAL);
      return;
    }
    if (pathname.includes("/reseller")) {
      const { email, userType, companyName, name } = state;
      if (!email || !userType) {
        navigate("/login");
        return;
      }
      setCheckedEmail(email);
      if (userType === RESSELLER_USER_TYPE.NOT_SALESFORCE_USER) {
        setStep(SIGNUP_STEP.RESELLER_SUBMISSION);
      } else {
        setStep(SIGNUP_STEP.RESELLER_SIGNUP);
        const names = (name || "").split(" ");
        setUserInfo((userInfo) => ({
          ...userInfo,
          companyName: companyName,
          firstName: names[0] || "",
          lastName:
            names.length < 4 && names.length > 1
              ? names.slice(1).join(" ")
              : "",
        }));
      }
      return;
    }
    if (pathname === "/reset") {
      setStep(SIGNUP_STEP.RESET_PASSWORD);
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const inviteEmail = params.get("email");
    if (
      inviteEmail !== undefined &&
      inviteEmail !== null &&
      checkEmpty(inviteEmail)
    ) {
      setCheckedEmail(toTrim(inviteEmail));
    }
    if (token === undefined || token == null) {
      navigate("/login");
    } else {
      getToken(token as string);
    }
  }, []);

  const mutationVerify = useMutation(postVerify, {
    onSuccess: (res: any) => {
      setMfaNumber(res.result.verifiedNumber);
      // setStep(SIGNUP_STEP.INPUT_VERIFY_CODE);
    },
    onError: () => {
      setIsValidated({
        ...intialValidate,
        phoneNumber: true,
      });
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.invalidPhoneNumber),
      }));
    },
  });

  const onSendVerify = () => {
    if (!checkEmpty(userInfo.phoneNumber)) {
      if (!isPhoneNumberValid(userInfo.phoneNumber)) {
        setIsValidated({
          ...intialValidate,
          phoneNumber: true,
        });
        setValidateInputMsg((prev) => ({
          ...prev,
          phoneNumber: intlString(lang.validateMsg.signup.phone.invalidFormat),
        }));
        // setValidateMsg(
        //   intl.formatMessage({
        //     id: "validateMsg.signup.phone.invalidFormat",
        //     defaultMessage:
        //       "The phone number you entered is in an invalid format.",
        //   })
        // );
        return true;
      } else {
        setIsValidated({
          ...intialValidate,
        });
        setIsPhoneVerified(false);
        setVerifyCode("");
        setValidateInputMsg((prev) => ({
          ...prev,
          phoneNumber: "",
        }));
        setIsValidated((prev) => ({
          ...prev,
          phoneNumber: false,
        }));
        mutationVerify.mutate({
          payload: {
            phoneNumber: userInfo.phoneNumber,
          },
        });
      }
    } else {
      setIsValidated({
        ...intialValidate,
        phoneNumber: true,
      });
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.profile.phone.empty),
      }));
    }
  };

  const onClickLogin = useCallback(() => {
    if (auth.user.userId) auth.logout();
    const searchParams = [
      ["accountId", checkedAccountId],
      ["email", checkedEmail],
    ]
      .filter(([key, value]) => Boolean(value))
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");
    window.location.replace(`/login?${searchParams}`);
  }, [auth, checkedAccountId, checkedEmail]);

  const onClickVerifyCancel = useCallback(() => {
    setIsValidated({
      ...intialValidate,
    });
    setIsPhoneVerified(false);
    setVerifyCode("");
    setUserInfo((info) => {
      return {
        ...info,
        phoneNumber: "",
        verifyPhone: false,
        use2faCode: "email",
        notifySms: false,
      };
    });
  }, [isValidated, isPhoneVerified, verifyCode, userInfo.phoneNumber]);

  const onChangeUserInfo = useCallback(
    (type: SIGNUP_UPDATE_TYPE, value: string | boolean) => {
      if (type === SIGNUP_UPDATE_TYPE.PASSWORD) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            password: value as string,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.CONFIRM_PASSWORD) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            confirmPassword: value as string,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.PHONE_NUMBER) {
        //핸드폰번호가 있다면 무조건 인증을 받아야한다.
        setIsVerifyPhoneNumber(value === "");
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            phoneNumber: value as string,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.FIRST_NAME) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            firstName: value as string,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.LAST_NAME) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            lastName: value as string,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.AGREEMENT) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            agreement: value as boolean,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.TWOFACTOR) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            use2faCode: value as "phone" | "email",
            notifySms:
              value === "phone"
                ? true
                : !userInfo.phoneNumber
                ? false
                : userInfo.notifySms,
          };
        });
      } else if (type === SIGNUP_UPDATE_TYPE.NOTIFYSMS) {
        setUserInfo((userInfo) => {
          return {
            ...userInfo,
            notifySms: value as boolean,
          };
        });
      }
    },
    [userInfo]
  );

  const onChangeVerifyCode = useCallback(
    (value: string) => {
      setVerifyCode(value);
    },
    [verifyCode]
  );

  const onClickVerify = useCallback(() => {
    if (mfaNumber === verifyCode) {
      setIsValidVerify(false);
      setIsPhoneVerified(true);
      setMfaNumber("");
      setIsVerifyPhoneNumber(true);
      setUserInfo((info) => ({
        ...info,
        verifyPhone: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: "",
      }));
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: false,
      }));
      setUserInfo((info) => ({
        ...info,
        use2faCode: "phone",
      }));
    } else {
      setIsValidVerify(true);

      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.verifyNumber),
      }));
      setIsVerifyPhoneNumber(false);
    }
  }, [mfaNumber, verifyCode]);

  const onClickBackLogin = useCallback(() => {
    navigate("/");
  }, []);

  const mutationResendCode = useMutation(postVerify, {
    onSuccess: (res: any) => {
      if (res.result.verifiedNumber !== undefined) {
        setMfaNumber(res.result.verifiedNumber);
      }
      notifySuccess(lang.label.signup.resendCode.success);
    },
    onError: () => {
      notifyError(lang.label.signup.resendCode.fail);
    },
  });

  const onClickResend = useCallback(() => {
    setIsValidVerify(false);
    setVerifyCode("");
    if (!checkEmpty(userInfo.phoneNumber)) {
      if (!isPhoneNumberValid(userInfo.phoneNumber)) {
        setIsValidated({
          ...intialValidate,
          phoneNumber: true,
        });
        setValidateInputMsg((prev) => ({
          ...prev,
          phoneNumber: intlString(lang.validateMsg.signup.phone.invalidFormat),
        }));
        return true;
      } else {
        mutationResendCode.mutate({
          payload: {
            phoneNumber: userInfo.phoneNumber,
          },
        });
      }
    } else {
      setIsValidated({
        ...intialValidate,
        phoneNumber: true,
      });
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.profile.phone.empty),
      }));
    }
  }, [mutationResendCode, userInfo.phoneNumber]);
  const [cloudLoading, setCloudLoading] = useState<boolean>(false);
  return (
    <SignupPagePresenter
      step={step}
      checkedEmail={checkedEmail}
      userInfo={userInfo}
      verifyCode={verifyCode}
      onChangeVerifyCode={onChangeVerifyCode}
      onClickVerify={onClickVerify}
      onClickLogin={onClickLogin}
      onClickVerifyCancel={onClickVerifyCancel}
      onChangeUserInfo={onChangeUserInfo}
      isValidPassword={isValidPassword}
      setIsValidPassword={setIsValidPassword}
      onClickBackLogin={onClickBackLogin}
      firstNameRef={firstNameRef}
      lastNameRef={lastNameRef}
      passwordRef={passwordRef}
      confirmPasswordRef={confirmPasswordRef}
      agreementRef={agreementRef}
      validateMsg={validateMsg}
      onClickResend={onClickResend}
      isValidVerify={isValidVerify}
      isLoadedToken={isLoadedToken}
      isExpiredToken={isExpiredToken}
      cloudLoading={cloudLoading}
      isPhoneVerified={isPhoneVerified}
      onSendVerify={onSendVerify}
      mfaNumber={mfaNumber}
      validateInputMsg={validateInputMsg}
      setIsValidated={setIsValidated}
      setValidateInputMsg={setValidateInputMsg}
      setUserInfo={setUserInfo}
      isVerifyPhoneNumber={isVerifyPhoneNumber}
      setValidateMsg={setValidateMsg}
      setStep={setStep}
      setIsValidVerify={setIsValidVerify}
      setIsResend={setIsResend}
      isResend={isResend}
      setCheckedEmail={setCheckedEmail}
    />
  );
}
